import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Link2, Search } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { errText } from "./shared";

const BASE = "/admin/rewards/fulfilment";

function OfferBadge({ offer, testid }) {
  if (!offer) return <span className="text-xs text-slate-400" data-testid={testid}>Not linked · brand website, no cashback tracking</span>;
  return (
    <span className="text-xs" data-testid={testid}>
      <b className="text-slate-900">{offer.store_name || "Merchant"}</b> · {offer.name}
      <span className={`ml-2 text-[10px] uppercase tracking-widest font-semibold ${offer.active ? "text-emerald-700" : "text-rose-700"}`}>
        {offer.active ? "Live" : `Not live (${offer.status || "no landing page"})`}
      </span>
    </span>
  );
}

function OfferPicker({ product, onClose, onSaved }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!product) return undefined;
    const t = setTimeout(async () => {
      try { setItems((await api.get(`${BASE}/campaign-options`, { params: { q: q || undefined } })).data.items || []); }
      catch { setItems([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [q, product]);
  useEffect(() => { if (product) setQ(""); }, [product]);

  const save = async (campaignId) => {
    setBusy(true);
    try {
      await api.patch(`${BASE}/products/${product.id}`, { campaign_id: campaignId });
      toast.success(campaignId ? `${product.brand} linked to the offer.` : `${product.brand} link removed.`);
      onSaved();
    } catch (e) { toast.error(errText(e, "Could not save the link")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!product} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="gc-offer-picker">
        <DialogHeader>
          <DialogTitle>Link {product?.brand} to a Cyannect offer</DialogTitle>
          <DialogDescription>"Shop with this Gift Card" will start this offer's tracked referral, so eligible purchases earn cashback.</DialogDescription>
        </DialogHeader>
        <label className="relative block">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search offers or merchants"
                 className="w-full pl-8 pr-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid="gc-offer-search" />
        </label>
        <ul className="max-h-72 overflow-y-auto divide-y divide-slate-100" data-testid="gc-offer-options">
          {items.length === 0 && <li className="py-6 text-center text-sm text-slate-500" data-testid="gc-offer-empty">No offers found.</li>}
          {items.map((o) => (
            <li key={o.id} className="py-2 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <OfferBadge offer={o} />
                {o.destination_host && <div className="text-[11px] text-slate-400 truncate">{o.destination_host}{o.provinces.length ? ` · ${o.provinces.join(", ")}` : ""}</div>}
              </div>
              <button disabled={!o.active || busy || product?.campaign_id === o.id} onClick={() => save(o.id)}
                      className="pf-btn-primary px-3 py-1 rounded-full text-xs font-semibold disabled:opacity-40" data-testid={`gc-offer-pick-${o.id}`}>
                {product?.campaign_id === o.id ? "Linked" : "Link"}
              </button>
            </li>
          ))}
        </ul>
        {product?.campaign_id && (
          <button onClick={() => save("")} disabled={busy} className="text-xs text-rose-700 font-semibold self-start" data-testid="gc-offer-unlink">
            Remove link (use the brand website instead)
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Admin picks which Cyannect offer each gift card brand opens from "Shop with this Gift Card". */
function Suggestion({ product, onAccept, busy }) {
  const s = product.suggested_offer;
  return (
    <div className="mt-1 flex flex-wrap items-center gap-2 rounded-lg border border-cyan-100 bg-cyan-50/50 px-2 py-1 text-xs" data-testid={`gc-brand-suggestion-${product.id}`}>
      <span className="text-[10px] uppercase tracking-widest font-semibold text-cyan-800">Suggested</span>
      <span className="text-slate-800"><b>{s.offer.store_name || "Merchant"}</b> · {s.offer.name}</span>
      <span className="text-slate-500" data-testid={`gc-brand-suggestion-reason-${product.id}`}>{s.reason_label}</span>
      <button disabled={busy} onClick={() => onAccept(product, s.offer)} data-testid={`gc-brand-accept-${product.id}`}
              className="pf-btn-primary px-3 py-0.5 rounded-full text-[11px] font-semibold disabled:opacity-50">Link</button>
    </div>
  );
}

export function BrandOfferLinks({ canEdit }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { setItems((await api.get(`${BASE}/products`)).data.items || []); } catch { setItems([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const accept = async (p, offer) => {
    setBusy(true);
    try {
      await api.patch(`${BASE}/products/${p.id}`, { campaign_id: offer.id });
      toast.success(`${p.brand} linked to ${offer.store_name || offer.name}.`);
      load();
    } catch (e) { toast.error(errText(e, "Could not link the offer")); }
    finally { setBusy(false); }
  };

  return (
    <div className="pf-card p-5" data-testid="gc-brand-offer-links">
      <div className="flex items-center gap-2 mb-1">
        <Link2 className="w-4 h-4 text-slate-500" />
        <h2 className="font-display text-lg text-slate-900">Brand offer links</h2>
      </div>
      <p className="text-xs text-slate-500 mb-3">Pick the Cyannect offer each brand opens from "Shop with this Gift Card". Unlinked brands open their own website without cashback tracking.</p>
      <ul className="divide-y divide-slate-100">
        {items.map((p) => (
          <li key={p.id} className="py-2 flex flex-wrap items-center justify-between gap-2" data-testid={`gc-brand-row-${p.id}`}>
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-900">{p.brand} <span className="text-[11px] text-slate-400 font-normal">{p.category}</span></div>
              <OfferBadge offer={p.linked_offer} testid={`gc-brand-offer-${p.id}`} />
              {canEdit && p.suggested_offer && <Suggestion product={p} onAccept={accept} busy={busy} />}
            </div>
            {canEdit && (
              <button onClick={() => setEditing(p)} className="text-xs px-3 py-1 rounded-full border border-cyan-200 text-cyan-800 font-semibold hover:bg-cyan-50"
                      data-testid={`gc-brand-link-${p.id}`}>{p.campaign_id ? "Change" : p.suggested_offer ? "Choose another" : "Link offer"}</button>
            )}
          </li>
        ))}
      </ul>
      <OfferPicker product={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </div>
  );
}
